import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import { settingsApi } from '../../api/settings.api';
import {
  ArrowRight,
  Calendar,
  ChevronDown,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  // ImagePlus,
  Instagram,
  LineChart,
  Menu,
  PenSquare,
  // Quote,
  Rocket,
  Sparkles,
  Target,
  Users,
  X,
} from 'lucide-react';

// type ImagePlaceholderProps = {
//   title: string;
//   aspectClassName: string;
//   helper?: string;
// };

// function ImagePlaceholder({ title, aspectClassName, helper }: ImagePlaceholderProps) {
//   return (
//     <div className="relative rounded-2xl bg-white border border-gray-200 shadow-sm overflow-hidden">
//       <div
//         className={`${aspectClassName} bg-[radial-gradient(circle_at_20%_20%,rgba(219,39,119,0.10),transparent_35%),radial-gradient(circle_at_80%_30%,rgba(61,90,134,0.12),transparent_40%),radial-gradient(circle_at_30%_80%,rgba(61,90,134,0.08),transparent_40%)] bg-gray-50 flex items-center justify-center`}
//       >
//         <div className="text-center px-8">
//           <div className="mx-auto w-12 h-12 rounded-xl bg-white/70 border border-white/40 shadow-sm flex items-center justify-center">
//             <ImagePlus className="w-6 h-6 text-gray-700" />
//           </div>
//           <p className="mt-3 text-sm font-semibold text-gray-900">{title}</p>
//           {helper && <p className="mt-1 text-xs text-gray-600">{helper}</p>}
//         </div>
//       </div>
//     </div>
//   );
// }

// type Testimonial = {
//   name: string;
//   role: string;
//   quote: string;
// };

type FaqItem = {
  question: string;
  answer: string;
};

function scrollToHash(hash: string) {
  if (!hash) return;

  const id = hash.startsWith('#') ? hash : `#${hash}`;
  const el = document.querySelector(id);
  if (!(el instanceof HTMLElement)) return;

  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function LandingPage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [hasMounted, setHasMounted] = useState(false);
  const [blogEnabled, setBlogEnabled] = useState(false);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => setHasMounted(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    settingsApi.getBlogEnabled()
      .then(setBlogEnabled)
      .catch(() => setBlogEnabled(false));
  }, []);

  const navLinks = useMemo(() => {
    const links = [
      { href: '#servicos', label: 'Serviços' },
      { href: '#resultados', label: 'Resultados' },
      // { href: '#portfolio', label: 'Portfólio' },
    ];

    if (blogEnabled) {
      links.push({ href: '/blog', label: 'Blog' });
    }

    links.push(
      { href: '#faq', label: 'Dúvidas' },
      { href: '#contato', label: 'Contato' }
    );

    return links;
  }, [blogEnabled]);

  // const testimonials: Testimonial[] = useMemo(
  //   () => [
  //     {
  //       name: 'Cliente',
  //       role: 'Negócio local',
  //       quote:
  //         'A Rota Estratégica trouxe clareza. Passei a postar com consistência e entender o que realmente gera resultado.',
  //     },
  //     {
  //       name: 'Cliente',
  //       role: 'Serviços',
  //       quote:
  //         'Eu não tinha tempo (nem direção). Agora tenho conteúdo planejado e uma rotina mais leve, sem improviso.',
  //     },
  //     {
  //       name: 'Cliente',
  //       role: 'Profissional liberal',
  //       quote:
  //         'As legendas e CTAs ficaram mais estratégicos. A comunicação ficou mais alinhada com minha marca.',
  //     },
  //   ],
  //   []
  // );

  const faqItems: FaqItem[] = useMemo(
    () => [
      {
        question: 'Quais são as opções de serviço?',
        answer:
          'Você tem duas opções: (1) Gestão completa de conteúdo — eu cuido do planejamento, acompanho o mês e faço ajustes por performance; quando necessário, também faço captação de fotos e vídeos. (2) Planejamento Estratégico — alternativa mais acessível e pontual: eu entrego o Planejamento Estratégico de 2 meses e você executa (grava/fotografa, cria as artes e publica). Tudo o que está na Rota Estratégica está incluído na Gestão completa.',
      },
      {
        question: 'O que está incluso na Rota Estratégica?',
        answer:
          'Um calendário de 2 meses (pagamento único de R$ 397,00) com temas e ideias por post, roteiros quando necessário (Reels/Stories), direção de criativos/referências e um checklist de execução. Ele é ideal para quem quer clareza do que postar e por quê — e vai produzir, editar e publicar por conta própria.',
      },
      {
        question: 'Na Gestão completa, você também faz fotos e vídeos?',
        answer:
          'Sim — quando necessário e alinhado no briefing. A ideia é destravar a execução: captação pontual (foto/vídeo), direção do que gravar e consistência no mês. A forma de captação (celular ou produção mais elaborada) depende do seu momento e da necessidade do conteúdo.',
      },
      {
        question: 'Preciso ter fotos e vídeos profissionais?',
        answer:
          'Não necessariamente. Podemos trabalhar com o que você já tem e orientar captação simples com celular. Se você escolher a Gestão completa, eu posso ajudar com captação quando necessário; na Rota Estratégica, você faz a captação e a execução seguindo a direção do planejamento.',
      },
      {
        question: 'Qual o prazo e como funciona a aprovação?',
        answer:
          'Começamos com briefing e objetivos. Depois disso, eu preparo o calendário com antecedência para você aprovar e, se necessário, ajustamos antes da execução. O prazo exato varia conforme agenda e volume, e eu te confirmo no primeiro contato.',
      },
      {
        question: 'Como funciona o valor?',
        answer:
          'A Rota Estratégica tem valor fixo (pagamento único de R$ 397,00 pelos 2 meses) — sem contrato e sem fidelidade. A Gestão completa de conteúdo depende do seu objetivo, volume e necessidade de captação — por isso é feita por orçamento.',
      },
    ],
    []
  );

  const heroPhrase = 'vender com clareza';

  const handleNavClick = (href: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!href.startsWith('#')) return;
    e.preventDefault();
    setIsMenuOpen(false);
    scrollToHash(href);
  };

  useEffect(() => {
    const ids = navLinks
      .map((l) => l.href)
      .filter((h) => h.startsWith('#'))
      .map((h) => h.slice(1));

    const sections = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el instanceof HTMLElement);

    if (!('IntersectionObserver' in window) || sections.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => (b.intersectionRatio ?? 0) - (a.intersectionRatio ?? 0));

        if (visible.length === 0) return;

        const id = (visible[0].target as HTMLElement).id;
        setActiveSection(id);
      },
      {
        root: null,
        rootMargin: '-20% 0px -70% 0px',
        threshold: [0.05, 0.1, 0.2, 0.3],
      }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [navLinks]);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/80 backdrop-blur border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to=""
            className="flex items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            aria-label="Ir para a página inicial"
          >
            <img
              src="/logo.svg?v=20260111"
              alt="Bruna Rodrigues"
              className="h-10 w-auto"
              width={247}
              height={40}
            />
            <div className="hidden xl:block">
              <p className="text-xs text-gray-500 whitespace-nowrap">Social Media e Estrategista</p>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            {/* Segmented nav (desktop) */}
            <nav className="hidden lg:flex items-center rounded-full bg-gray-50 border border-gray-200 p-1 shadow-sm">
              {navLinks.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className={
                    item.href.slice(1) === activeSection
                      ? 'px-4 py-2 rounded-full text-sm font-semibold bg-white text-gray-900 shadow-sm'
                      : 'px-4 py-2 rounded-full text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-white transition-colors'
                  }
                  aria-current={item.href.slice(1) === activeSection ? 'page' : undefined}
                  onClick={handleNavClick(item.href)}
                >
                  {item.label}
                </a>
              ))}
            </nav>

            {/* Main CTA */}
            <a
              href="#contato"
              className="hidden lg:inline-flex btn-primary text-sm items-center gap-2 whitespace-nowrap"
              onClick={handleNavClick('#contato')}
            >
              Solicitar orçamento
              <ArrowRight className="w-4 h-4" />
            </a>

            {/* Mobile menu */}
            <button
              type="button"
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
              aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={isMenuOpen}
              aria-controls="landing-mobile-menu"
              onClick={() => setIsMenuOpen((v) => !v)}
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {isMenuOpen && (
          <div id="landing-mobile-menu" className="lg:hidden border-t border-gray-100 bg-white">
            <div className="max-w-6xl mx-auto px-6 py-4">
              <div className="grid gap-2">
                {navLinks.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    className="px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-sm font-semibold text-gray-800 hover:bg-white transition-colors"
                    onClick={handleNavClick(item.href)}
                  >
                    {item.label}
                  </a>
                ))}

                <div className="mt-2 flex items-center justify-between">
                  <a
                    href="#contato"
                    className="btn-primary w-full inline-flex items-center justify-center gap-2"
                    onClick={handleNavClick('#contato')}
                  >
                    Solicitar orçamento
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      <main>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-primary-50/70 via-white to-white" />
        <div className="pointer-events-none absolute -top-24 right-0 h-80 w-80 rounded-full bg-gradient-to-br from-primary-200/50 via-secondary-100/50 to-secondary-200/30 blur-3xl animate-br-float" />
        <div className="pointer-events-none absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-gradient-to-br from-secondary-200/40 via-white to-primary-100/40 blur-3xl animate-br-float-slow" />
        <div className="relative max-w-6xl mx-auto px-6 pt-6 pb-12 sm:pt-10 sm:pb-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <p
              className={
                hasMounted
                  ? 'inline-flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-100 rounded-full px-4 py-2 motion-safe:animate-br-fade-up'
                  : 'inline-flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-100 rounded-full px-4 py-2'
              }
              style={{ animationDelay: '60ms' }}
            >
              <CheckCircle2 className="w-4 h-4" />
              Gestão profissional de redes sociais
            </p>

            <h1
              className={
                hasMounted
                  ? 'mt-4 text-4xl sm:text-5xl font-sumana font-bold tracking-tight motion-safe:animate-br-fade-up'
                  : 'mt-4 text-4xl sm:text-5xl font-sumana font-bold tracking-tight'
              }
              style={{ animationDelay: '120ms' }}
            >
              Conteúdo estratégico e consistente para
              <br />
              <span className="text-primary-700">{heroPhrase}</span>
            </h1>
            <p className="mt-4 text-gray-600 text-lg leading-relaxed">
              Planejamento, criação e acompanhamento mensal com um processo simples e organizado.
              Você foca no seu negócio — eu cuido do conteúdo.
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: ClipboardCheck, title: 'Planejamento do mês', desc: 'Calendário editorial com direção clara.' },
                { icon: Rocket, title: 'Conteúdo com objetivo', desc: 'CTAs, narrativa e consistência para vender.' },
                { icon: LineChart, title: 'Ajustes por dados', desc: 'O que funciona entra; o resto melhora.' },
                { icon: Clock, title: 'Rotina leve', desc: 'Processo simples, sem correria e sem achismo.' },
              ].map((card) => (
                <div
                  key={card.title}
                  className={
                    hasMounted
                      ? 'rounded-2xl bg-white border border-gray-200 shadow-sm p-4 transition-transform duration-300 hover:-translate-y-1 hover:shadow-md motion-safe:animate-br-fade-up'
                      : 'rounded-2xl bg-white border border-gray-200 shadow-sm p-4'
                  }
                  style={{ animationDelay: `${220 + ['Planejamento do mês', 'Conteúdo com objetivo', 'Ajustes por dados', 'Rotina leve'].indexOf(card.title) * 80}ms` }}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-50 border border-primary-100 flex items-center justify-center">
                      <card.icon className="w-5 h-5 text-primary-700" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{card.title}</p>
                      <p className="text-sm text-gray-600 mt-0.5">{card.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a
                href="#contato"
                className="btn-primary inline-flex items-center justify-center gap-2"
                onClick={handleNavClick('#contato')}
              >
                Agendar uma conversa
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#resultados"
                className="btn-secondary inline-flex items-center justify-center"
                onClick={handleNavClick('#resultados')}
              >
                Ver resultados e trabalhos
              </a>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {[
                'Briefing objetivo',
                'Calendário editorial',
                'Roteiros + ideias',
                'Direção de identidade',
                'Análise de performance',
              ].map((badge) => (
                <span
                  key={badge}
                  className="inline-flex items-center rounded-full bg-gray-50 border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700"
                >
                  {badge}
                </span>
              ))}
            </div>
          </div>

          <div className="relative lg:self-center">
            <div className="pointer-events-none absolute -inset-8 bg-primary-100/60 blur-3xl rounded-full" />

            {/* Pedestal/background for a cut-out PNG */}
            <div className="pointer-events-none absolute inset-x-6 bottom-4 h-24 rounded-full bg-gradient-to-r from-primary-100/70 via-white to-secondary-100/60 blur-xl" />

            <div
              className={
                hasMounted
                  ? 'relative motion-safe:animate-br-fade-up'
                  : 'relative'
              }
              style={{ animationDelay: '180ms' }}
            >
              <div className="relative rounded-2xl bg-white border border-gray-200 shadow-sm overflow-hidden">
                <div className="aspect-[3/4] bg-gray-50">
                  <img
                    src="/IMG_3099-420.webp"
                    srcSet="/IMG_3099-240.webp 240w, /IMG_3099-320.webp 320w, /IMG_3099-420.webp 420w, /IMG_3099-540.webp 540w, /IMG_3099-720.webp 720w, /IMG_3099-1080.webp 1080w"
                    sizes="(max-width: 640px) 90vw, (max-width: 1024px) 420px, 420px"
                    alt="Foto da Bruna Rodrigues"
                    className="w-full h-full object-cover"
                    loading="eager"
                    decoding="async"
                    width={420}
                    height={560}
                  />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-3">
                {[
                  { label: 'Processo', value: 'Organizado' },
                  { label: 'Foco', value: 'Conversão' },
                  { label: 'Entrega', value: 'Mensal' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="rounded-2xl bg-white border border-gray-200 shadow-sm p-3 text-center transition-transform duration-300 hover:-translate-y-1"
                  >
                    <p className="text-xs text-gray-500">{item.label}</p>
                    <p className="text-sm font-bold tracking-tight text-gray-900 mt-0.5">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 pb-10">
          <a
            href="#servicos"
            onClick={handleNavClick('#servicos')}
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
          >
            Ver como funciona
            <ChevronDown className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Services */}
      <section id="servicos" className="max-w-6xl mx-auto px-6 py-14 scroll-mt-28">
        <div className="flex items-start justify-between gap-8 flex-col lg:flex-row">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-100 rounded-full px-4 py-2">
              <Sparkles className="w-4 h-4" />
              Um jeito simples de trabalhar
            </p>
            <h2 className="mt-4 text-3xl font-sumana font-bold">Serviços</h2>
            <p className="mt-3 text-gray-600 leading-relaxed">
              Você escolhe o formato ideal: <strong>Gestão completa de conteúdo</strong> (eu cuido de tudo)
              ou <strong>Rota Estratégica</strong> (alternativa mais acessível e pontual: Planejamento Estratégico de 2 meses para você executar).
            </p>
          </div>

          <a
            href="#contato"
            className="hidden lg:inline-flex items-center justify-center gap-2 rounded-xl bg-secondary-600 text-white px-4 py-2.5 font-semibold hover:bg-secondary-700 transition-colors"
            onClick={handleNavClick('#contato')}
          >
            Solicitar orçamento
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              step: '01',
              title: 'Briefing + direção',
              desc: 'Entendo objetivos, público e posicionamento para guiar todo o mês.',
              icon: Target,
            },
            {
              step: '02',
              title: 'Calendário + roteiros',
              desc: 'Temas e roteiros organizados para você aprovar com antecedência.',
              icon: Calendar,
            },
            {
              step: '03',
              title: 'Ajustes por performance',
              desc: 'Olho os dados e ajusto o que traz resultado (sem achismo).',
              icon: LineChart,
            },
          ].map((item) => (
            <div key={item.step} className="rounded-2xl bg-white border border-gray-200 shadow-sm p-5">
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-xl bg-primary-50 border border-primary-100 flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-primary-700" />
                </div>
                <span className="text-xs font-semibold text-gray-500">{item.step}</span>
              </div>
              <p className="mt-4 font-semibold text-gray-900">{item.title}</p>
              <p className="mt-1 text-sm text-gray-600">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="relative rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_10%_0%,rgba(219,39,119,0.10),transparent_45%)]" />
            <div className="relative p-6 md:p-7 grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
              <div className="md:col-span-5">
                <p className="inline-flex items-center gap-2 text-xs font-semibold text-primary-800 bg-primary-50 border border-primary-100 rounded-full px-3 py-1">
                  <Users className="w-4 h-4" />
                  Completo
                </p>
                <h3 className="mt-4 text-2xl font-sumana font-bold">Gestão completa de conteúdo</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Tudo da Rota Estratégica + acompanhamento do mês. Quando necessário, eu também faço captação de fotos e vídeos.
                </p>
              </div>

              <div className="md:col-span-7">
                <p className="text-sm font-semibold text-gray-900">Inclui</p>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    'Tudo do Rota Estratégica',
                    'Legendas + direção de CTA',
                    'Acompanhamento e ajustes por performance',
                    'Captação de fotos e vídeos (quando necessário)',
                  ].map((t) => (
                    <div key={t} className="flex items-start gap-2 rounded-xl bg-gray-50 border border-gray-200 px-3 py-2">
                      <CheckCircle2 className="w-4 h-4 text-primary-700 mt-0.5" />
                      <p className="text-sm text-gray-700">{t}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-5">
                  <a
                    href="#contato"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-primary-700 transition-colors"
                    onClick={handleNavClick('#contato')}
                  >
                    Quero a gestão completa
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="relative rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_10%_0%,rgba(61,90,134,0.10),transparent_45%)]" />
            <div className="relative p-6 md:p-7 grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
              <div className="md:col-span-5">
                <p className="inline-flex items-center gap-2 text-xs font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded-full px-3 py-1">
                  <Calendar className="w-4 h-4" />
                  Mais acessível
                </p>
                <h3 className="mt-4 text-2xl font-sumana font-bold">Rota Estratégica</h3>
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-secondary-50 border border-secondary-200 px-3 py-1 whitespace-nowrap leading-none">
                  <span className="sm:hidden text-[11px] font-semibold text-secondary-900">Único:</span>
                  <span className="sm:hidden text-[11px] font-bold text-secondary-900">R$ 397</span>
                  <span className="sm:hidden text-[11px] font-semibold text-secondary-900">(2 meses)</span>

                  <span className="hidden sm:inline text-xs font-semibold text-secondary-900">Pagamento único</span>
                  <span className="hidden sm:inline text-xs font-bold text-secondary-900">R$ 397</span>
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  Planejamento Estratégico de 2 meses.<br />Eu planejo, você executa.
                </p>

                <p className="mt-3 text-xs text-gray-500">
                  Compra única, sem contrato e sem fidelidade. Tudo o que está na Rota Estratégica está incluído na Gestão completa de conteúdo.
                </p>
              </div>

              <div className="md:col-span-7">
                <p className="text-sm font-semibold text-gray-900">Inclui</p>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    'Rota Estratégica (2 meses)',
                    'Temas + ideias por post',
                    'Roteiros (Reels/Stories) quando necessário',
                    'Direção de criativos e referências',
                  ].map((t) => (
                    <div key={t} className="flex items-start gap-2 rounded-xl bg-gray-50 border border-gray-200 px-3 py-2">
                      <CheckCircle2 className="w-4 h-4 text-primary-700 mt-0.5" />
                      <p className="text-sm text-gray-700">{t}</p>
                    </div>
                  ))}
                </div>


                <div className="mt-5">
                  <a
                    href="#contato"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-secondary-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-secondary-700 transition-colors whitespace-nowrap"
                    onClick={handleNavClick('#contato')}
                  >
                    Quero a Rota Estratégica
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section id="resultados" className="bg-white border-y border-gray-100 scroll-mt-28">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="flex items-end justify-between gap-6 flex-col sm:flex-row">
            <div className="max-w-2xl">
              <p className="inline-flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-100 rounded-full px-4 py-2">
                <LineChart className="w-4 h-4" />
                Resultados e clareza
              </p>
              <h2 className="mt-4 text-3xl font-sumana font-bold">O que você ganha com um processo bem feito</h2>
              <p className="mt-3 text-gray-600 leading-relaxed">
                Menos improviso. Mais consistência. Uma comunicação alinhada com sua marca — e com ajustes baseados em dados.
              </p>
            </div>

            <a
              href="#contato"
              onClick={handleNavClick('#contato')}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-secondary-600 text-white px-4 py-2.5 font-semibold hover:bg-secondary-700 transition-colors"
            >
              Quero entender meu caso
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                title: 'Consistência no mês',
                desc: 'Calendário editorial para você saber o que postar e por quê.',
                icon: Calendar,
              },
              {
                title: 'Conteúdo com intenção',
                desc: 'Roteiros e legendas com direção de CTA e narrativa.',
                icon: Target,
              },
              {
                title: 'Evolução contínua',
                desc: 'Análise do que performa e ajustes sem achismo.',
                icon: LineChart,
              },
            ].map((card) => (
              <div key={card.title} className="rounded-2xl bg-white border border-gray-200 shadow-sm p-6">
                <div className="w-11 h-11 rounded-xl bg-primary-50 border border-primary-100 flex items-center justify-center">
                  <card.icon className="w-5 h-5 text-primary-700" />
                </div>
                <p className="mt-4 text-lg font-semibold text-gray-900">{card.title}</p>
                <p className="mt-2 text-sm text-gray-600">{card.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: 'Planejamento', value: '30 dias à frente' },
              { label: 'Organização', value: 'Conteúdo por etapas' },
              { label: 'Direção', value: 'Identidade + tom' },
            ].map((stat) => (
              <div key={stat.label} className="rounded-2xl bg-gray-50 border border-gray-200 p-5">
                <p className="text-xs font-semibold text-gray-500">{stat.label}</p>
                <p className="mt-1 text-xl font-bold tracking-tight text-gray-900">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="relative rounded-2xl bg-white border border-gray-200 shadow-sm overflow-hidden">
            <div className="aspect-[4/3] bg-gray-50">
              <img
                src="/IMG_3100.JPG"
                alt="Foto profissional da Bruna Rodrigues"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-sumana font-bold">Sobre a Bruna</h2>
            <p className="mt-3 text-gray-600 leading-relaxed">
              Social media com foco em estratégia e execução organizada — para marcas que querem crescer com consistência.
              Trabalho com processo claro (briefing → calendário → ajustes por dados) e conteúdo com intenção.
            </p>

            <div className="mt-6 space-y-3">
              {[
                'Estratégia com foco em objetivos reais do negócio',
                'Conteúdo consistente, com identidade e clareza',
                'Processo organizado (briefing → execução → análise)',
              ].map((text) => (
                <div key={text} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary-700 mt-0.5" />
                  <p className="text-gray-700">{text}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex gap-3 flex-col sm:flex-row">
              <a href="#contato" className="btn-primary inline-flex items-center justify-center gap-2">
                Falar com a Bruna
                <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#portfolio" className="btn-secondary inline-flex items-center justify-center">
                Ver portfólio
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="flex items-end justify-between gap-6 flex-col sm:flex-row">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-100 rounded-full px-4 py-2">
              <Quote className="w-4 h-4" />
              Depoimentos
            </p>
            <h2 className="mt-4 text-3xl font-sumana font-bold">O que dizem após organizar o conteúdo</h2>
            <p className="mt-3 text-gray-600 leading-relaxed">
              Substitua os textos abaixo por feedbacks reais quando você tiver. A estrutura já está pronta.
            </p>
          </div>

          <a
            href="#contato"
            onClick={handleNavClick('#contato')}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-white border border-gray-200 px-4 py-2.5 font-semibold text-gray-800 hover:bg-gray-50 transition-colors"
          >
            Quero um orçamento
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {testimonials.map((t) => (
            <div key={t.quote} className="rounded-2xl bg-white border border-gray-200 shadow-sm p-6">
              <Quote className="w-5 h-5 text-primary-700" />
              <p className="mt-3 text-sm text-gray-700 leading-relaxed">“{t.quote}”</p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm font-semibold text-gray-900">{t.name}</p>
                <p className="text-xs text-gray-500">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="portfolio" className="max-w-6xl mx-auto px-6 py-14 scroll-mt-28">
        <div className="flex items-end justify-between gap-6 flex-col sm:flex-row">
          <div>
            <h2 className="text-3xl font-sumana font-bold">Portfólio</h2>
            <p className="mt-2 text-gray-600">
              Adicione aqui exemplos de posts, campanhas e resultados. Você pode trocar os placeholders por imagens reais.
            </p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, idx) => (
            <div key={idx} className="rounded-2xl bg-white border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-4">
                <ImagePlaceholder
                  title={`Imagem do case #${idx + 1}`}
                  helper="Ex.: print do feed / antes e depois / resultado"
                  aspectClassName="aspect-[4/3] rounded-xl"
                />
              </div>
              <div className="p-5">
                <p className="font-semibold">Nome do cliente / projeto</p>
                <p className="text-sm text-gray-600 mt-1">
                  Objetivo, formato do trabalho e 1 resultado (ex.: alcance, leads, vendas, engajamento).
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
      */}

      {/* FAQ */}
      <section id="faq" className="bg-gray-50 border-y border-gray-100 scroll-mt-28">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-100 rounded-full px-4 py-2">
              <PenSquare className="w-4 h-4" />
              Perguntas frequentes
            </p>
            <h2 className="mt-4 text-3xl font-sumana font-bold">Dúvidas comuns</h2>
            <p className="mt-3 text-gray-600 leading-relaxed">
              Respostas rápidas sobre formatos, o que está incluso e próximos passos.
            </p>
          </div>

          <div className="mt-8 grid gap-3">
            {faqItems.map((item, index) => {
              const isOpen = openFaqIndex === index;
              return (
                <div key={item.question} className="rounded-2xl bg-white border border-gray-200 shadow-sm overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                    onClick={() => setOpenFaqIndex((prev) => (prev === index ? null : index))}
                    aria-expanded={isOpen}
                  >
                    <span className="text-sm font-semibold text-gray-900">{item.question}</span>
                    <ChevronDown
                      className={
                        isOpen
                          ? 'w-5 h-5 text-gray-600 transition-transform rotate-180'
                          : 'w-5 h-5 text-gray-600 transition-transform'
                      }
                    />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-gray-600 leading-relaxed">{item.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="rounded-2xl bg-gradient-to-br from-primary-50 via-white to-secondary-50 border border-gray-200 shadow-sm p-8 md:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="inline-flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-100 rounded-full px-4 py-2">
                <Sparkles className="w-4 h-4" />
                Conteúdo gratuito
              </p>
              <h2 className="mt-4 text-3xl font-sumana font-bold">Receba dicas de conteúdo e estratégia</h2>
              <p className="mt-3 text-gray-600 leading-relaxed">
                Assine a newsletter e receba insights práticos sobre redes sociais, planejamento e criação de conteúdo direto no seu e-mail.
              </p>
            </div>

            <div className="flex justify-center lg:justify-end">
              <iframe
                src="https://socialbrunarodrigues.substack.com/embed"
                width="480"
                height="320"
                style={{ border: '1px solid #EEE', background: 'white', borderRadius: '12px', maxWidth: '100%' }}
                frameBorder="0"
                scrolling="no"
                title="Formulário de inscrição na newsletter"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA / Contact */}
      <section id="contato" className="bg-gradient-to-b from-white to-primary-50 border-t border-gray-100 scroll-mt-28">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="rounded-2xl bg-white border border-gray-200 shadow-sm p-8 md:p-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-3xl font-sumana font-bold">Vamos planejar o seu conteúdo?</h2>
                <p className="mt-3 text-gray-600">
                  Me conte seu objetivo e o momento da sua marca. Eu te respondo com clareza de processo e proposta.
                </p>

                <div className="mt-6 space-y-3">
                  <a
                    href="https://wa.me/5563984277517"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 rounded-xl bg-secondary-50 border border-secondary-200 hover:bg-secondary-100/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-500 focus-visible:ring-offset-2"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-secondary-600 text-white flex items-center justify-center shadow-sm">
                        <FaWhatsapp className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-secondary-900">WhatsApp</p>
                        <p className="text-xs text-secondary-900/70">Clique para falar comigo: +55 63 98427-7517</p>
                      </div>
                    </div>
                  </a>
                  <a
                    href="https://linqdoo.com/brunarodrigues"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 rounded-xl bg-primary-50 border border-primary-200 hover:bg-primary-100/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary-600 via-primary-600 to-primary-500 text-white flex items-center justify-center shadow-sm">
                        <Instagram className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold">Instagram</p>
                        <p className="text-xs text-primary-900">@bru1alm</p>
                      </div>
                    </div>
                  </a>
                </div>
              </div>

              <div className="relative rounded-2xl bg-white border border-gray-200 shadow-sm overflow-hidden">
                <div className="aspect-[4/3] bg-gray-50">
                  <img
                    src="/IMG_4682.PNG"
                    alt="Imagem do CTA"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    width={1200}
                    height={900}
                  />
                </div>
              </div>
            </div>
          </div>

          <footer className="mt-10 text-sm text-gray-500">
            <p>
              © {new Date().getFullYear()} Bruna Rodrigues • Todos os direitos reservados
              <span className="mx-2">•</span>
              <Link
                to="/login"
                className="font-semibold text-primary-700 hover:text-primary-800 transition-colors"
              >
                Login
              </Link>
            </p>
          </footer>
        </div>
      </section>

      </main>

      {/* Floating WhatsApp CTA */}
      <a
        href="https://wa.me/5563984277517"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-30 inline-flex items-center gap-3 rounded-full bg-secondary-600 text-white px-4 py-3 shadow-lg hover:bg-secondary-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-500 focus-visible:ring-offset-2"
      >
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/15">
          <FaWhatsapp className="w-5 h-5" aria-hidden="true" />
        </span>
        <span className="text-sm font-semibold">WhatsApp</span>
      </a>
    </div>
  );
}
